import React, { useState } from 'react';
import { Navigation } from '../components/Navigation';
import {
  Phone,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  CheckCircle,
  XCircle,
  Loader2,
  MapPin,
  MessageCircle,
  Image,
  Edit3,
} from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useGetData, useUpdateFirestoreItem } from '../hooks';
import { useParams, useNavigate, Link, generatePath } from 'react-router-dom';
import {
  categoryMessages,
  COLLECTIONS,
  ROLES,
  LISTING_ITEM_STATUSES,
  locationMessages,
  ROUTES,
} from '../constants';
import { UserAvatar } from '../components/UserAvatar.tsx';
import { useAuth } from '../providers/AuthProvider.tsx';
import { LayoutWrapper } from '../components/LayoutWrapper.tsx';

const USER_BLOCK_ID = 'user-block';

const ServiceDetailsSkeleton = () => (
  <div className='min-h-screen bg-gray-50'>
    <div className='bg-white shadow-sm'>
      <Navigation theme='dark' />
    </div>

    <div className='container mx-auto px-4 py-8'>
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
        {/* Main Content Skeleton */}
        <div className='lg:col-span-2 space-y-6'>
          <div className='bg-white rounded-xl shadow-sm p-6'>
            {/* Header Skeleton */}
            <div className='flex items-start justify-between mb-4'>
              <div className='space-y-2 w-full max-w-md'>
                <div className='h-8 bg-gray-200 rounded-md animate-pulse' />
                <div className='h-4 bg-gray-200 rounded-md w-2/3 animate-pulse' />
              </div>
            </div>

            {/* Image Skeleton */}
            <div className='relative bg-gray-200 rounded-lg h-[300px] sm:h-[500px] md:h-[600px] mb-4 animate-pulse'>
              <div className='absolute inset-0 flex items-center justify-center'>
                <Loader2 className='w-8 h-8 text-gray-400 animate-spin' />
              </div>
            </div>

            {/* Thumbnails Skeleton */}
            <div className='flex justify-center space-x-2 mb-4'>
              {[1, 2, 3].map((_, index) => (
                <div
                  key={index}
                  className='w-20 h-20 bg-gray-200 rounded-lg animate-pulse'
                />
              ))}
            </div>

            {/* Price and Button Skeleton */}
            <div className='flex items-center justify-between pt-4 border-t border-gray-100'>
              <div className='h-8 bg-gray-200 rounded-md w-24 animate-pulse' />
              <div className='h-10 bg-gray-200 rounded-lg w-32 animate-pulse' />
            </div>
          </div>

          {/* Description Skeleton */}
          <div className='bg-white rounded-xl shadow-sm p-6'>
            <div className='h-6 bg-gray-200 rounded-md w-48 mb-4 animate-pulse' />
            <div className='space-y-2'>
              <div className='h-4 bg-gray-200 rounded-md animate-pulse' />
              <div className='h-4 bg-gray-200 rounded-md w-3/4 animate-pulse' />
              <div className='h-4 bg-gray-200 rounded-md w-5/6 animate-pulse' />
            </div>
          </div>

          {/* Reviews Skeleton */}
          <div className='bg-white rounded-xl shadow-sm p-6'>
            <div className='h-6 bg-gray-200 rounded-md w-48 mb-6 animate-pulse' />
            <div className='space-y-6'>
              {[1, 2].map((_, index) => (
                <div
                  key={index}
                  className='flex items-start space-x-4 pb-6 border-b border-gray-100 last:border-0 last:pb-0'
                >
                  <div className='w-12 h-12 bg-gray-200 rounded-full animate-pulse' />
                  <div className='flex-1 space-y-2'>
                    <div className='h-4 bg-gray-200 rounded-md w-1/4 animate-pulse' />
                    <div className='h-4 bg-gray-200 rounded-md w-full animate-pulse' />
                    <div className='h-4 bg-gray-200 rounded-md w-3/4 animate-pulse' />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar Skeleton */}
        <div className='space-y-6'>
          <div className='bg-white rounded-xl shadow-sm p-6'>
            <div className='flex items-center space-x-4 mb-6'>
              <div className='w-16 h-16 bg-gray-200 rounded-full animate-pulse' />
              <div className='space-y-2'>
                <div className='h-4 bg-gray-200 rounded-md w-32 animate-pulse' />
                <div className='h-4 bg-gray-200 rounded-md w-24 animate-pulse' />
              </div>
            </div>
            <div className='h-10 bg-gray-200 rounded-lg animate-pulse' />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const ServiceDetails = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const { id } = useParams();
  const { data, isPending } = useGetData(id!);
  const { user } = useAuth();
  const { data: providerData, isPending: isPendingUserData } = useGetData(
    data?.providerUuid,
    COLLECTIONS.users,
  );
  const { mutate: updateServiceItemStatus } = useUpdateFirestoreItem();
  const isAdmin = !!user && user?.role === ROLES.admin;

  // Check if the current user is the owner of this service
  const isOwner = !!user && !!data && user.uid === data.providerUuid;

  if (
    (!data && !isPending) ||
    (!!data &&
      data?.status !== LISTING_ITEM_STATUSES.active &&
      !isAdmin &&
      !isOwner)
  ) {
    navigate('/404');
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === data.images.length - 1 ? 0 : prev + 1,
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? data.images.length - 1 : prev - 1,
    );
  };

  // Handler for status change
  const handleStatusChange = (
    serviceId: string,
    newStatus: keyof typeof LISTING_ITEM_STATUSES,
  ) => {
    updateServiceItemStatus({
      id: serviceId,
      newData: {
        status: newStatus,
      },
    });
  };

  if (isPending || isPendingUserData) {
    return <ServiceDetailsSkeleton />;
  }

  return (
    <LayoutWrapper>
      <div className='container mx-auto py-8'>
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
          {/* Main Content */}
          <div className='lg:col-span-2 space-y-6'>
            {/* Service Header */}
            <div className='bg-white rounded-xl shadow-sm p-6'>
              <div className='flex items-start justify-between mb-4'>
                <div>
                  <h1 className='text-3xl font-bold text-gray-900 mb-2 break-all'>
                    {data.title}
                  </h1>
                  <div className='flex items-center space-x-4 text-sm text-gray-500'>
                    <Link
                      to={`${generatePath(
                        ROUTES.services,
                      )}?category=${encodeURIComponent(data.category)}`}
                      className='hover:underline text-blue-600'
                    >
                      {formatMessage(
                        categoryMessages[
                          `category${data.category}` as keyof typeof categoryMessages
                        ],
                      )}
                    </Link>
                    {data?.location && (
                      <>
                        <span className='w-1 h-1 rounded-full bg-gray-300'></span>
                        <Link
                          to={`${generatePath(
                            ROUTES.services,
                          )}?location=${encodeURIComponent(data.location)}`}
                          className='flex items-center hover:underline text-blue-600'
                        >
                          <MapPin className='w-4 h-4 mr-1 text-gray-400' />
                          {formatMessage(
                            locationMessages[
                              `location${data.location}` as keyof typeof locationMessages
                            ],
                          )}
                        </Link>
                      </>
                    )}
                  </div>
                </div>
                {/* Admin controls and Owner edit button */}
                <div className='flex items-center space-x-2'>
                  {/* Owner Edit Button */}
                  {isOwner && (
                    <Link
                      to={ROUTES.editService.replace(':id', data.id)}
                      state={{ prevRoute: ROUTES.serviceDetails }}
                      className='text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-gray-100 transition-colors'
                      title='Редактировать'
                    >
                      <Edit3 className='w-5 h-5' />
                    </Link>
                  )}

                  {/* Admin-only Approve/Reject Buttons (always visible for admin) */}
                  {isAdmin && (
                    <>
                      {![
                        LISTING_ITEM_STATUSES.paused,
                        LISTING_ITEM_STATUSES.active,
                      ].includes(data.status) && (
                        <button
                          onClick={() =>
                            handleStatusChange(
                              data.id,
                              LISTING_ITEM_STATUSES.active,
                            )
                          }
                          className='text-green-600 hover:text-green-900 p-2 rounded-full hover:bg-gray-100 transition-colors'
                          title='Одобрить'
                        >
                          <CheckCircle className='w-5 h-5' />
                        </button>
                      )}
                      {data.status !== LISTING_ITEM_STATUSES.rejected && (
                        <button
                          onClick={() =>
                            handleStatusChange(
                              data.id,
                              LISTING_ITEM_STATUSES.rejected,
                            )
                          }
                          className='text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-gray-100 transition-colors'
                          title='Отклонить'
                        >
                          <XCircle className='w-5 h-5' />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Image Gallery */}
              <div className='relative bg-gray-100 rounded-lg overflow-hidden mb-4'>
                {data.images && data.images.length > 0 ? (
                  <>
                    <div className='flex justify-center items-center h-[300px] sm:h-[500px] md:h-[600px] bg-gray-100'>
                      <img
                        src={data.images[currentImageIndex]}
                        alt={data.title}
                        className='max-w-full max-h-full object-contain'
                      />
                    </div>
                    {data.images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className='absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full hover:bg-white transition-colors z-10 shadow-md'
                        >
                          <ChevronLeft className='w-5 h-5' />
                        </button>
                        <button
                          onClick={nextImage}
                          className='absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full hover:bg-white transition-colors z-10 shadow-md'
                        >
                          <ChevronRight className='w-5 h-5' />
                        </button>

                        {/* Image Counter */}
                        <div className='absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-3 py-1 rounded-full text-white text-sm'>
                          {currentImageIndex + 1} / {data.images.length}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className='h-[300px] sm:h-[500px] md:h-[600px] flex items-center justify-center bg-gray-100'>
                    <div className='text-center p-8'>
                      <Image className='w-16 h-16 text-gray-400 mx-auto mb-4' />
                      <p className='text-gray-500'>
                        <FormattedMessage
                          id='noImage'
                          defaultMessage='Нет изображения'
                        />
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {data?.images && data.images.length > 1 && (
                <div className='flex justify-center flex-wrap gap-2 mb-4'>
                  {data?.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                        index === currentImageIndex
                          ? 'border-blue-600 opacity-100'
                          : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className='w-full h-full object-cover'
                      />
                    </button>
                  ))}
                </div>
              )}

              <div className='flex items-center justify-between pt-4 border-t border-gray-100'>
                <div className='text-3xl font-bold text-blue-600'>
                  {data.price}RON
                  <span className='text-sm font-normal text-gray-500 ml-1'>
                    / услуга
                  </span>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className='bg-white rounded-xl shadow-sm p-6'>
              <h2 className='text-xl font-semibold mb-4'>
                <FormattedMessage
                  id='serviceDetails.desc'
                  defaultMessage='Описание услуги'
                />
              </h2>
              <div className='prose max-w-none'>
                {data.description.split('\n\n').map((paragraph, index) => (
                  <div key={index} className='mb-4'>
                    {paragraph.includes('•') ? (
                      <ul className='list-none space-y-2'>
                        {paragraph.split('\n').map((line, lIndex) => (
                          <li
                            key={lIndex}
                            className={
                              line.startsWith('•')
                                ? "pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-blue-600"
                                : ''
                            }
                          >
                            {line.replace('• ', '')}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className='text-gray-600'>{paragraph}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className='space-y-6' id={USER_BLOCK_ID}>
            {/* Provider Card */}
            <div className='bg-white rounded-xl shadow-sm p-6'>
              <div className='flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6'>
                {/* Аватар - фиксированный размер */}
                <div className='flex-shrink-0 w-16 h-16 rounded-full overflow-hidden'>
                  <UserAvatar
                    photoURL={providerData?.photoUrl}
                    displayName={providerData?.displayName}
                    email={providerData?.email}
                    size='lg'
                  />
                </div>

                <div className='flex-1 min-w-0'>
                  <div className='flex items-center flex-wrap gap-2'>
                    <h3 className='font-semibold text-gray-900 truncate max-w-full'>
                      {providerData?.displayName || providerData?.email}
                    </h3>
                    <CheckCircle className='w-4 h-4 flex-shrink-0 text-blue-600' />
                  </div>

                  {providerData?.location && (
                    <div className='text-sm text-gray-500 flex items-center mt-1'>
                      <MapPin className='w-4 h-4 mr-1' />
                      {formatMessage(
                        locationMessages[
                          `location${providerData.location}` as keyof typeof locationMessages
                        ],
                      )}
                    </div>
                  )}
                </div>
              </div>

              {showContactInfo ? (
                <div className='space-y-4'>
                  {(data?.phoneNumber || providerData?.phoneNumber) && (
                    <a
                      href={`tel:${
                        data?.phoneNumber || providerData?.phoneNumber
                      }`}
                      className='flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors'
                    >
                      <div className='flex items-center flex-1 min-w-0'>
                        <Phone className='w-5 h-5 flex-shrink-0 text-gray-600 mr-3' />
                        <span className='text-gray-900 truncate'>
                          {data?.phoneNumber || providerData?.phoneNumber}
                        </span>
                      </div>
                      <span className='text-sm text-blue-600 flex-shrink-0 ml-2'>
                        <FormattedMessage
                          id='service.call'
                          defaultMessage='Позвонить'
                        />
                      </span>
                    </a>
                  )}

                  {/* WhatsApp */}
                  {(data?.phoneNumber || providerData?.phoneNumber) && (
                    <a
                      href={`https://wa.me/${(
                        data?.phoneNumber || providerData?.phoneNumber
                      ).replace(/[^0-9]/g, '')}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors'
                    >
                      <div className='flex items-center flex-1 min-w-0'>
                        <MessageCircle className='w-5 h-5 flex-shrink-0 text-gray-600 mr-3' />
                        <span className='text-gray-900 truncate'>WhatsApp</span>
                      </div>
                      <span className='text-sm text-blue-600 flex-shrink-0 ml-2'>
                        Написать
                      </span>
                    </a>
                  )}

                  {/* Telegram */}
                  {(data?.telegram || providerData?.telegram) && (
                    <a
                      href={`https://t.me/${(
                        data?.telegram || providerData?.telegram
                      ).replace('@', '')}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors'
                    >
                      <div className='flex items-center flex-1 min-w-0'>
                        <MessageSquare className='w-5 h-5 flex-shrink-0 text-gray-600 mr-3' />
                        <span className='text-gray-900 truncate'>
                          {data?.telegram || providerData?.telegram}
                        </span>
                      </div>
                      <span className='text-sm text-blue-600 flex-shrink-0 ml-2'>
                        Telegram
                      </span>
                    </a>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowContactInfo(true)}
                  className='w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors'
                >
                  <FormattedMessage
                    id='service.showContactInfo'
                    defaultMessage='Показать контакты'
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </LayoutWrapper>
  );
};
